/**
 * The platform live-invalidation registry: which query-key roots read each
 * table, and the apply a change-feed page goes through (spec 2026-09-25-001
 * §5.4 and §5.5). Generalised from `invalidate-bms-tables.ts` (research 07
 * §1.5), whose four exports now delegate here.
 *
 * TWO SOURCES, UNIONED. The generated seed (`seed.generated.ts`, research 08
 * §7) says who reads each table on the day it was generated; `registerReader`
 * adds the readers that declare themselves at render. A root is identified by
 * its JSON, so two registrations of one root collapse.
 *
 * THE BMS WRITE PATH IS NOT THROTTLED AND NEVER SEES THE GOVERNED SEED.
 * `invalidateBmsTables` reads its own KNOWN_READERS plus `registeredRootsFor`
 * and invalidates at once, as it always has. Only `apply`, the feed's entry,
 * reads the generated seed and goes through the (client, root) throttle with
 * `cancelRefetch: false`.
 */
import { ACCESS_ROOTS, SEED_ROWS } from './seed.generated'
import { createRootThrottle } from './throttle'
import {
  ACCESS_TABLES,
  IDENTITY_QUERY_PREFIX,
  THROTTLE_INTERVAL_MS,
  type ApplyResult,
  type InvalidatingClient,
  type QueryRoot,
  type SeedRow,
  type TableName,
} from './types'

/** Serialised root, so equal roots collapse. */
type RootId = string

const rootsById = new Map<RootId, QueryRoot>()
const seededByTable = new Map<TableName, Set<RootId>>()
const registeredByTable = new Map<TableName, Set<RootId>>()

const remember = (root: QueryRoot): RootId => {
  const id: RootId = JSON.stringify(root)
  if (!rootsById.has(id)) rootsById.set(id, [...root])
  return id
}

const addTo = (store: Map<TableName, Set<RootId>>, table: TableName, id: RootId | undefined): void => {
  let ids = store.get(table)
  if (ids === undefined) {
    ids = new Set()
    store.set(table, ids)
  }
  if (id !== undefined) ids.add(id)
}

/** Add static rows to the store. A row with no roots still marks its table as known. */
export const seed = (rows: ReadonlyArray<SeedRow>): void => {
  for (const row of rows) {
    addTo(seededByTable, row.table, undefined)
    for (const root of row.roots) addTo(seededByTable, row.table, remember(root))
  }
}

/**
 * Declare that the query family rooted at `root` reads `table`. Idempotent, so
 * a reader may call it on every render.
 */
export const registerReader = (table: TableName, root: QueryRoot): void => {
  addTo(registeredByTable, table, remember(root))
}

/** The roots that read `table`: the seed unioned with the registrations, seed first. */
export const rootsFor = (table: TableName): ReadonlyArray<QueryRoot> => {
  const ids = new Set<RootId>([
    ...(seededByTable.get(table) ?? []),
    ...(registeredByTable.get(table) ?? []),
  ])
  const roots: Array<QueryRoot> = []
  for (const id of ids) {
    const root = rootsById.get(id)
    if (root !== undefined) roots.push(root)
  }
  return roots
}

/**
 * The roots declared through `registerReader` alone, without the seed. The BMS
 * write path reads this beside its own KNOWN_READERS, so a BMS mutation never
 * reaches the governed seed's roots (which only the feed's `apply` uses).
 */
export const registeredRootsFor = (table: TableName): ReadonlyArray<QueryRoot> => {
  const roots: Array<QueryRoot> = []
  for (const id of registeredByTable.get(table) ?? []) {
    const root = rootsById.get(id)
    if (root !== undefined) roots.push(root)
  }
  return roots
}

const isKnownTable = (table: TableName): boolean =>
  seededByTable.has(table) || registeredByTable.has(table)

/** Test-only: drop every registration. The seed stays, as it does in a tab. */
export const resetReaders = (): void => {
  registeredByTable.clear()
}

const throttle = createRootThrottle(THROTTLE_INTERVAL_MS)

/** Whether `root` is `prefix` or a key under it, as React Query matches a filter. */
const underRoot = (root: QueryRoot, prefix: QueryRoot): boolean =>
  prefix.length <= root.length && prefix.every((part, at) => JSON.stringify(part) === JSON.stringify(root[at]))

const invalidate = (client: InvalidatingClient, root: QueryRoot): Promise<void> =>
  client.invalidateQueries({ queryKey: root }, { cancelRefetch: false })

/** True when the identity refetch already reaches every query under `root`: the identity key is a prefix of it. */
const coveredByIdentity = (root: QueryRoot): boolean =>
  root.length >= IDENTITY_QUERY_PREFIX.length &&
  IDENTITY_QUERY_PREFIX.every((part, i) => JSON.stringify(root[i]) === JSON.stringify(part))

const IDENTITY_FAMILY_ID: RootId = JSON.stringify(['identity'])

/**
 * The access roots less any the identity refetch already covers. The generated
 * list carries `['identity']`, the family the identity key sits in; it is
 * dropped by name, because invalidating it after the awaited refetch would
 * refetch identity twice. A broader root that is not that one entry is kept,
 * since it reaches queries the identity key does not; the registry test pins
 * that the generated list holds no other.
 */
const accessRootsAfterIdentity: ReadonlyArray<QueryRoot> = ACCESS_ROOTS.filter(
  (root) => !coveredByIdentity(root) && JSON.stringify(root) !== IDENTITY_FAMILY_ID,
)

/**
 * Apply one delivered page's table names to one client (spec §5.5, steps 2 to
 * 4). The access tables leave the page before `rootsFor`; the rest are merged,
 * mapped, and each root goes through the (client, root) throttle. When an
 * access table is named, identity is refetched first and every root, the
 * access roots included, waits for it to settle, because a list refetched
 * before identity would be filtered by the old grants.
 *
 * Resolves when identity and the leading-edge invalidations have settled;
 * trailing edges fire later on their own.
 *
 * `rereadByWriter` NAMES THE ROOTS A WRITER IN THIS TAB WILL RE-READ once this
 * page has been applied, when the page is the one it asked for
 * (`requestOwnWritePoll`). Those roots, and every root under one by prefix,
 * are left to it: its re-read starts after the page came back, so it sees
 * every change the page names, another user's included, and re-reading them
 * here as well was the second read of every own write. Every other root on
 * the page goes the ordinary way.
 */
export const apply = async (
  client: InvalidatingClient,
  tables: ReadonlyArray<TableName>,
  options: {
    /**
     * Asked once identity has been refetched (when an access table is named)
     * and not before, so a writer that stopped waiting before then is not left
     * anything: it re-read under the old grants, and the page re-reads its
     * roots after identity as it does any other.
     */
    readonly rereadByWriter?: () => ReadonlyArray<QueryRoot>
    /**
     * Called once the roots left to the writer are out of the page and, when
     * an access table is named, identity has been refetched: the writer may
     * re-read from then on, without waiting for every other root's refetch.
     */
    readonly onDecided?: () => void
  } = {},
): Promise<ApplyResult> => {
  const unknownTables: Array<TableName> = []
  const ids = new Set<RootId>()
  let access = false
  for (const table of new Set(tables)) {
    if (ACCESS_TABLES.has(table)) {
      access = true
      continue
    }
    if (!isKnownTable(table)) {
      unknownTables.push(table)
      continue
    }
    for (const root of rootsFor(table)) ids.add(JSON.stringify(root))
  }

  if (access) {
    // cancelRefetch TRUE here, and only here. A fetch of identity already in
    // flight (a focus refetch, say) started before the grant changed; with
    // false, query-core hands that fetch back and every root below would run
    // against the old grants.
    await client.invalidateQueries({ queryKey: IDENTITY_QUERY_PREFIX }, { cancelRefetch: true })
    for (const root of accessRootsAfterIdentity) ids.add(remember(root))
    for (const id of [...ids]) {
      const root = rootsById.get(id)
      if (root !== undefined && coveredByIdentity(root)) ids.delete(id)
    }
  }

  // Decided here and answered by `onDecided` below with no await between them,
  // so the set left out is exactly the writers told to re-read.
  const leftToWriter = options.rereadByWriter?.() ?? []
  if (leftToWriter.length > 0) {
    for (const id of [...ids]) {
      const root = rootsById.get(id)
      if (root !== undefined && leftToWriter.some((mine) => underRoot(root, mine))) ids.delete(id)
    }
  }

  const fired: Array<unknown> = []
  for (const id of ids) {
    const root = rootsById.get(id)
    if (root !== undefined) fired.push(throttle.run(client, id, () => invalidate(client, root)))
  }
  options.onDecided?.()
  await Promise.all(fired)

  return { roots: ids.size, unknownTables, access }
}

/**
 * A resync page (spec §5.5 step 1): invalidate everything, once, and drop the
 * trailing calls the throttle was holding for this client, which the full
 * invalidation already covers.
 */
export const applyResync = async (client: InvalidatingClient): Promise<void> => {
  throttle.cancel(client)
  await client.invalidateQueries(undefined, { cancelRefetch: false })
}

seed(SEED_ROWS)
